import React, {useCallback} from 'react';
import {View, StyleSheet, ScrollView, Linking} from 'react-native';
import {Text, Divider} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const ContactUsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const openLink = useCallback(url => {
    Linking.openURL(url).catch(() => {});
  }, []);

  const ContactRow = ({icon, label, value, onPress}) => (
    <View
      style={styles.contactRow}
      accessible
      accessibilityRole={onPress ? 'link' : 'text'}>
      <MaterialCommunityIcons
        name={icon}
        size={22}
        color={colors.primary}
        style={styles.contactIcon}
      />
      <View style={styles.contactInfo}>
        <Text style={styles.contactLabel}>{label}</Text>
        <Text
          style={[styles.contactValue, onPress && styles.contactLink]}
          onPress={onPress}>
          {value}
        </Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <GradientHeader title={t('contactUs.title')} onMenu={() => navigation.dispatch(DrawerActions.openDrawer())} />

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* About section */}
        <View style={styles.card}>
          <View style={styles.brandRow}>
            <MaterialCommunityIcons
              name="scissors-cutting"
              size={28}
              color={colors.primary}
            />
            <Text style={styles.brandName}>{t('contactUs.brandName')}</Text>
          </View>
          <Text style={styles.aboutText}>{t('contactUs.aboutText')}</Text>
          <Text style={styles.companyName}>{t('contactUs.companyName')}</Text>
        </View>

        {/* Get in Touch */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t('contactUs.getInTouch')}</Text>

          <ContactRow
            icon="phone"
            label="Phone"
            value={t('contactUs.phone')}
            onPress={() => openLink('tel:' + t('contactUs.phone').replace(/\s/g, ''))}
          />
          <Divider style={styles.divider} />

          <ContactRow
            icon="email"
            label="Email"
            value={t('contactUs.email')}
            onPress={() => openLink('mailto:' + t('contactUs.email'))}
          />
          <Divider style={styles.divider} />

          <ContactRow
            icon="whatsapp"
            label="WhatsApp"
            value={t('contactUs.whatsapp')}
            onPress={() =>
              openLink(
                'https://wa.me/' +
                  t('contactUs.whatsapp').replace(/[^0-9]/g, ''),
              )
            }
          />
        </View>

        {/* Follow Us */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t('contactUs.followUs')}</Text>

          <ContactRow
            icon="instagram"
            label="Instagram"
            value={t('contactUs.instagram')}
            onPress={() =>
              openLink(
                'https://instagram.com/' +
                  t('contactUs.instagram').replace('@', ''),
              )
            }
          />
          <Divider style={styles.divider} />

          <ContactRow
            icon="web"
            label="Website"
            value={t('contactUs.website')}
            onPress={() => openLink(t('contactUs.website'))}
          />
        </View>

        {/* Visit Us */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t('contactUs.visitUs')}</Text>

          <ContactRow
            icon="map-marker"
            label="Address"
            value={
              t('contactUs.addressLine1') +
              '\n' +
              t('contactUs.addressLine2')
            }
          />
        </View>

        {/* Legal */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Legal</Text>

          <ContactRow
            icon="shield-lock-outline"
            label="Privacy Policy"
            value="fabtailor.in/privacy-policy"
            onPress={() => openLink('https://fabtailor.in/privacy-policy')}
          />
          <Divider style={styles.divider} />

          <ContactRow
            icon="account-remove-outline"
            label="Delete Account"
            value="Request account deletion"
            onPress={() => openLink('https://fabtailor.in/delete-account')}
          />
        </View>

        <View style={{height: spacing.xxl}} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  content: {flex: 1, padding: spacing.md},

  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },

  // Brand
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: spacing.sm,
  },
  brandName: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.primary,
  },
  aboutText: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 22,
    marginBottom: spacing.sm,
  },
  companyName: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
  },

  // Section
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.md,
  },

  // Contact row
  contactRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 8,
  },
  contactIcon: {
    marginRight: 12,
    marginTop: 2,
  },
  contactInfo: {flex: 1},
  contactLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 2,
  },
  contactValue: {
    fontSize: 15,
    color: colors.text,
    lineHeight: 22,
  },
  contactLink: {
    color: colors.primary,
    fontWeight: '500',
  },
  divider: {marginVertical: 4},
});

export default ContactUsScreen;
